<template>
  <v-card outlined class="ma-2" elevation="2">
    <v-card-title class="text-h5 title-header"
      >{{ $t("llm_config.langChain_config.title") }}
    </v-card-title>
    <v-card-text>
      <v-row justify="center" align="center">
        <v-col cols="12" md="12">
          <v-card outlined height="100%">
            <v-card-title>{{
              $t("llm_config.langChain_config.langchain_settings_title")
            }}</v-card-title>
            <v-card-text>
              <v-col cols="12">
                <v-row justify="start" dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="langchain_endpoint"
                      :items="langchain_endpoints"
                      :label="
                        $t(
                          'llm_config.langChain_config.langchain_params.langchain_endpoint'
                        )
                      "
                      :rules="form_roles.text"
                      :loading="available_llm_models_loading"
                      prepend-inner-icon="mdi-lan"
                      outlined
                      required
                      light
                      :menu-props="{ bottom: true, offsetY: true }"
                      hide-details
                    >
                      <template v-slot:item="{ item }">
                        <span>{{ item.text }}:</span>
                        <span class="caption ml-1 font-weight-bold">
                          ({{ maskDns(item.value) }})
                        </span>
                      </template>
                      <template v-slot:selection="{ item }">
                        <span>{{ item.text }}: {{ maskDns(item.value) }}</span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="utc_offset"
                      :label="
                        $t(
                          'llm_config.langChain_config.langchain_params.utc_offset_label'
                        )
                      "
                      :placeholder="
                        $t(
                          'llm_config.langChain_config.langchain_params.utc_offset_placeholder'
                        )
                      "
                      type="number"
                      :rules="form_roles.number"
                      prepend-inner-icon="mdi-clock-outline"
                      outlined
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="12">
          <v-card outlined height="100%">
            <v-card-title>{{
              $t("llm_config.langChain_config.provider_title")
            }}</v-card-title>
            <v-card-text>
              <v-col cols="12">
                <v-row justify="start" dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="llm_provider"
                      :items="llm_providers"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.provider_label'
                        )
                      "
                      :placeholder="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.provider_placeholder'
                        )
                      "
                      :rules="form_roles.text"
                      prepend-inner-icon="mdi-robot-vacuum-variant"
                      clearable
                      outlined
                      required
                      light
                      :menu-props="{ bottom: true, offsetY: true }"
                      hide-details
                    >
                    </v-select>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="llm_provider_api_key"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.api_key_label'
                        )
                      "
                      :placeholder="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.api_key_placeholder'
                        )
                      "
                      :rules="form_roles.text"
                      outlined
                      required
                      :type="show_api_key ? 'text' : 'password'"
                      @click:append="show_api_key = !show_api_key"
                      :append-icon="show_api_key ? 'mdi-eye' : 'mdi-eye-off'"
                      prepend-inner-icon="mdi-key-variant"
                    ></v-text-field>
                  </v-col>
                  <template>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="llm_provider_url"
                        :label="
                          $t(
                            'llm_config.langChain_config.ai_provider_param.url_label'
                          )
                        "
                        :placeholder="
                          $t(
                            'llm_config.langChain_config.ai_provider_param.url_placeholder'
                          )
                        "
                        :rules="form_roles.text"
                        prepend-inner-icon="mdi-web"
                        outlined
                        required
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="llm_provider_api_version"
                        :label="
                          $t(
                            'llm_config.langChain_config.ai_provider_param.api_version_label'
                          )
                        "
                        :placeholder="
                          $t(
                            'llm_config.langChain_config.ai_provider_param.api_version_placeholder'
                          )
                        "
                        :rules="form_roles.text"
                        outlined
                        required
                        prepend-inner-icon="mdi-key-variant"
                      ></v-text-field>
                    </v-col>
                  </template>
                </v-row>
              </v-col>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="12" v-if="available_llm_models">
          <v-card
            outlined
            height="100%"
            :loading="available_llm_models_loading"
          >
            <v-card-title>{{
              $t("llm_config.langChain_config.llm_models_title")
            }}</v-card-title>
            <v-card-subtitle class="red--text">
              {{ $t("llm_config.langChain_config.llm_models_subtitle") }}
            </v-card-subtitle>
            <v-card-text>
              <v-col cols="12">
                <v-row justify="start" dense>
                  <v-col cols="12" md="6">
                    <v-openai-model-select
                      :value="llm_answer_model"
                      :form_roles="form_roles"
                      @input="llm_answer_model = $event"
                      :openaiModels="openaiModels"
                      :allowedModels="
                        available_llm_models.llm_answer_models || []
                      "
                      :extraExternalModels="[]"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_answer_model_label'
                        )
                      "
                      :hint="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_answer_model_hint'
                        )
                      "
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-openai-model-select
                      :value="llm_worker_model"
                      :form_roles="form_roles"
                      @input="llm_worker_model = $event"
                      :openaiModels="openaiModels"
                      :allowedModels="
                        available_llm_models.llm_worker_models || []
                      "
                      :extraExternalModels="[]"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_worker_model_label'
                        )
                      "
                      :hint="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_worker_model_hint'
                        )
                      "
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <!-- FIXME: remove conditions this after the tests complited -->
                    <v-openai-model-select
                      :value="llm_reranker_model"
                      :form_roles="form_roles"
                      @input="llm_reranker_model = $event"
                      :openaiModels="openaiModels"
                      :allowedModels="
                        available_llm_models.llm_reranker_models || []
                      "
                      :extraExternalModels="[]"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_reranker_model_label'
                        )
                      "
                      :hint="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_reranker_model_hint'
                        )
                      "
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <!-- FIXME: remove conditions this after the tests complited -->
                    <v-openai-model-select
                      :value="llm_product_model"
                      :form_roles="form_roles"
                      @input="llm_product_model = $event"
                      :openaiModels="openaiModels"
                      :allowedModels="
                        available_llm_models.llm_product_models || []
                      "
                      :extraExternalModels="[]"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_product_model_label'
                        )
                      "
                      :hint="
                        $t(
                          'llm_config.langChain_config.ai_models.llm_product_model_hint'
                        )
                      "
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-openai-model-select
                      :value="embeddings_model"
                      :form_roles="form_roles"
                      @input="embeddings_model = $event"
                      :openaiModels="openaiModels"
                      :allowedModels="['text-embedding-3-large']"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_models.embeddings_model_label'
                        )
                      "
                      :hint="
                        $t(
                          'llm_config.langChain_config.ai_models.embeddings_model_hint'
                        )
                      "
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      class="pt-1"
                      v-model="verbosity"
                      @change="verbosity = $event"
                      :items="verbosity_list"
                      :label="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.verbosity_label'
                        )
                      "
                      :placeholder="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.verbosity_placeholder'
                        )
                      "
                      :hint="
                        $t(
                          'llm_config.langChain_config.ai_provider_param.verbosity_hint'
                        )
                      "
                      persistent-hint
                      :rules="form_roles.text"
                      prepend-inner-icon="mdi-format-list-bulleted-type"
                      clearable
                      outlined
                      :elevation="0"
                      required
                      small-chips
                      light
                      :menu-props="{ bottom: true, offsetY: true }"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import OpenaiModelSelector from "./OpenaiModelSelector.vue";
export default {
  name: "LangChainConfig",
  components: {
    "v-openai-model-select": OpenaiModelSelector,
  },
  data: () => ({
    langchain_endpoints: [
      {
        text: "Test",
        value:
          "http://a80710c75f5664a24842802cc97371e4-2124848182.eu-west-3.elb.amazonaws.com/rag_streaming",
        disabled: false,
      },
      {
        text: "Staging",
        value:
          "http://ac02b9b92f49648ee8f400c1274ff243-262826994.eu-west-3.elb.amazonaws.com/rag_streaming",
        disabled: false,
      },
      {
        text: "Production",
        value:
          "http://aec876a89abd34e1aa6118b2a93ddda9-232431280.eu-west-3.elb.amazonaws.com/rag_streaming",
        disabled: false,
      },
    ],
    llm_providers: [
      {
        text: "Azure OpenAI",
        value: "azure_openai",
        disabled: false,
      },
      {
        text: "OpenAI",
        value: "openai",
        disabled: true,
      },
      {
        text: "Mistral",
        value: "mistral",
        disabled: true,
      },
    ],
    verbosity_list: [
      {
        text: "Low",
        value: "low",
      },
      {
        text: "Medium",
        value: "medium",
      },
      {
        text: "High",
        value: "high",
      },
    ],
    show_api_key: false,
    available_llm_models: undefined,
    available_llm_models_loading: false,
  }),
  props: {
    openaiConfig: {
      type: Object,
      required: true,
    },
    form_roles: {
      type: Object,
      required: true,
    },
    openaiModels: {
      type: Array,
      default: () => [],
    },
  },
  async created() {
    if (!this.openaiConfig?.langchain_settings?.ai_provider_param) {
      this.$set(this.openaiConfig.langchain_settings, "ai_provider_param", {});
    }
    this.available_llm_models_loading = true;
    this.available_llm_models = await this.$store.dispatch(
      "FETCH_AVAILABLE_LLM_MODELS",
      this.openaiConfig.langchain_settings.endpoint
    );
    this.available_llm_models_loading = false;
  },
  watch: {
    //FIXME: remove this after the tests complited
    async langchain_endpoint(newVal) {
      this.available_llm_models_loading = true;
      this.available_llm_models = await this.$store.dispatch(
        "FETCH_AVAILABLE_LLM_MODELS",
        newVal
      );
      this.available_llm_models_loading = false;
    },
  },
  computed: {
    // Computed properties can be added here if needed
    llm_provider_url: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_provider_url || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_provider_url",
          value
        );
      },
    },
    llm_provider: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_provider || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_provider",
          value
        );
      },
    },
    llm_provider_api_key: {
      get() {
        return this.openaiConfig.api_key || null;
      },
      set(value) {
        this.$set(this.openaiConfig, "api_key", value);
      },
    },
    llm_provider_api_version: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_provider_api_version || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_provider_api_version",
          value
        );
      },
    },
    llm_answer_model: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_answer_model || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_answer_model",
          value
        );
      },
    },
    llm_worker_model: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_worker_model || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_worker_model",
          value
        );
      },
    },
    llm_reranker_model: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_reranker_model || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_reranker_model",
          value
        );
      },
    },
    llm_product_model: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.llm_product_model || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "llm_product_model",
          value
        );
      },
    },
    embeddings_model: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param
            ?.embeddings_model || null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "embeddings_model",
          value
        );
      },
    },
    verbosity: {
      get() {
        return (
          this.openaiConfig.langchain_settings.ai_provider_param?.verbosity ||
          null
        );
      },
      set(value) {
        this.$set(
          this.openaiConfig.langchain_settings.ai_provider_param,
          "verbosity",
          value
        );
      },
    },
    langchain_endpoint: {
      get() {
        return this.openaiConfig.langchain_settings?.endpoint || null;
      },
      set(value) {
        this.$set(this.openaiConfig.langchain_settings, "endpoint", value);
      },
    },
    utc_offset: {
      get() {
        return this.openaiConfig.langchain_settings?.utc_offset || 0;
      },
      set(value) {
        value = Number(value);
        this.$set(this.openaiConfig.langchain_settings, "utc_offset", value);
      },
    },
  },
  methods: {
    maskDns(value) {
      if (!value) return value;
      const match = value.match(/^(https?:\/\/)([^./]+)(.*)$/);
      if (!match) return value;
      const [, protocol, firstLabel, rest] = match;
      const visibleChars = 6;
      return `${protocol}${firstLabel.slice(0, visibleChars)}****${rest}`;
    },
  },
};
</script>

<style scoped>
.title-header {
  font-weight: bold;
  color: #1141ce !important;
}
</style>
